
import React, { useState } from 'react';
import { Button } from '../ui/button';
import { CheckCircle, Copy, CheckCheck } from 'lucide-react';
import { useForm } from '../../context/FormContext';
import { useToast } from '@/hooks/use-toast';
import { useNavigate } from 'react-router-dom';
import { formatDate, formatTime } from '@/utils/reportFormatters';

const FormCompleted: React.FC = () => {
  const { formData } = useForm();
  const { toast } = useToast();
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  
  const resetForm = () => {
    navigate('/');
  };

  // Helper function to safely convert any form value to a displayable string
  const safeToString = (value: string | string[] | boolean | File[] | undefined): string => {
    if (value === undefined || value === null) {
      return '-';
    }
    if (typeof value === 'string') {
      return value || '-';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    if (Array.isArray(value)) {
      if (value.length === 0) {
        return '-';
      }
      
      // Handle File arrays (check if first item is a File)
      if (value[0] instanceof File) {
        const files = value as File[];
        return files.map(file => file.name).join(', ');
      }
      
      // Handle string arrays
      return (value as string[]).join(', ');
    }
    
    return String(value);
  };

  // Extract problems encountered as a comma-separated string
  const getProblemsEncountered = () => {
    const problems = formData.problems_encountered as string[] || [];
    let problemsString = problems.join(', ');
    
    // Add "other" problems if provided
    if (formData.problems_encountered_other) {
      problemsString += problemsString ? `, ${formData.problems_encountered_other as string}` : formData.problems_encountered_other as string;
    }
    
    return problemsString || 'ไม่มี';
  };

  // Extract solutions applied as a comma-separated string
  const getSolutionsApplied = () => {
    const solutions = formData.solutions_applied as string[] || [];
    let solutionsString = solutions.join(', ');
    
    // Add "other" solutions if provided
    if (formData.solutions_applied_other) {
      solutionsString += solutionsString ? `, ${formData.solutions_applied_other as string}` : formData.solutions_applied_other as string;
    }
    
    return solutionsString || 'ไม่มี';
  };

  
  // Generate the report as a template literal
  const generateReportText = (): string => {
    const studentNickname = safeToString(formData.student_nickname);
    const studentFullName = safeToString(formData.student_full_name);
    const subject = safeToString(formData.subject);
    const teacherId = safeToString(formData.teacher_id);
    const topicLearned = safeToString(formData.topic_learned);
    const knowledgeRating = safeToString(formData.knowledge_rating);
    const knowledgeBehavior = safeToString(formData.knowledge_behavior);
    const skillsRating = safeToString(formData.skills_rating);
    const skillsBehavior = safeToString(formData.skills_behavior);
    const attitudeRating = safeToString(formData.attitude_rating);
    const attitudeBehavior = safeToString(formData.attitude_behavior);
    const studentImprovement = safeToString(formData.student_improvement || '-');
    const problemsEncountered = getProblemsEncountered();
    const solutionsApplied = getSolutionsApplied();
    const homeworkAssigned = safeToString(formData.homework_assigned || '-');
    const nextTopic = safeToString(formData.next_topic || '-');
    const classDate = formData.start_date ? formatDate(formData.start_date as string) : formatDate(formData.teaching_date as string);
    const classTime = formData.start_date ? formatTime(formData.start_date as string) : formData.teaching_time_slot;
    const endTime = formData.end_date ? formatTime(formData.end_date as string) : '';

    return `แจ้งพัฒนาการเรียนของ: น้อง${studentNickname} :${studentFullName}
  

📅 วันที่: ${classDate} เวลา ${classTime} - ${endTime}
📚 วิชา: ${subject}
👩‍🏫 ครูผู้สอน: ${teacherId}
✏️ หัวข้อบทเรียน: ${topicLearned}
📌 ระดับพัฒนาการของน้องใน 3 มิติ

🔹 ระดับพัฒนาการมิติความรู้ (Knowledge): ${knowledgeRating}
- พฤติกรรมที่สะท้อนถึง: ${knowledgeBehavior}
🔹 ระดับพัฒนาการมิติทักษะ (Skills): ${skillsRating}
- พฤติกรรมที่สะท้อนถึง: ${skillsBehavior}
🔹 ระดับพัฒนาการทัศนคติ (Attitude): ${attitudeRating}
- พฤติกรรมที่สะท้อนถึง: ${attitudeBehavior}

📌 สำหรับวันนี้น้องมีพัฒนาการที่เพิ่มขึ้นคือ: ${studentImprovement}

📌 ปัญหาที่เกิดกับน้องในการเรียน: ${problemsEncountered}

📌 แนวทางที่ใช้แก้ไขในคลาส: ${solutionsApplied}

📌 การบ้าน / แบบฝึกหัดที่ให้น้องวันนี้: ${homeworkAssigned}

📌 เรื่องถัดไปที่จะเรียน: ${nextTopic}

รายงานฉบับนี้เป็นการสรุปพัฒนาการเรียนรู้ของน้องจากคุณครู เพื่อให้ผู้ปกครองสามารถติดตามความก้าวหน้าของบุตรหลานได้ หากมีข้อสงสัยเกี่ยวกับการเรียนหรืออยากพูดคุยเพิ่มเติม สามารถแจ้งครูได้เสมอนะคะ/ครับ 😊`;
  };

  // Handle copying to clipboard
  const handleCopy = async () => {
    const text = generateReportText();
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast({
        title: "คัดลอกรายงานแล้ว",
        description: "คุณสามารถวางข้อความในที่ที่ต้องการได้",
      });
      
      // Reset the copied state after 2 seconds
      setTimeout(() => {
        setCopied(false);
      }, 2000);
    } catch (err) {
      console.error('Failed to copy text: ', err);
      toast({
        title: "ไม่สามารถคัดลอกได้",
        description: "โปรดลองอีกครั้ง",
        variant: "destructive",
      });
    }
  };

  const reportText = generateReportText();

  return (
    <div className="max-w-3xl mx-auto">
      <div className="flex justify-center mb-6">
        <CheckCircle className="h-16 w-16 text-green-500" />
      </div>
      <h2 className="text-2xl font-bold mb-4 text-center">ส่งแบบฟอร์มเรียบร้อยแล้ว</h2>
      
      {/* Summary Report Card */}
      <div className="bg-white border rounded-lg shadow-md p-6 mb-8">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-xl font-bold">สรุปรายงานการสอน</h3>
          <Button 
            onClick={handleCopy} 
            variant="outline" 
            className="flex items-center gap-2"
          >
            {copied ? <CheckCheck size={16} className="text-green-500" /> : <Copy size={16} />}
            {copied ? "คัดลอกแล้ว" : "คัดลอกรายงาน"}
          </Button>
        </div>
        
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
          <pre className="whitespace-pre-wrap break-words text-sm">
            {reportText}
          </pre>
        </div>
      </div>
      
      <div className="flex justify-center">
        <Button onClick={resetForm} className="bg-form-blue hover:bg-form-light-blue">
          กลับไปหน้าแรก
        </Button>
      </div>
    </div>
  );
};

export default FormCompleted;
